import React, { useState } from 'react';
import { supabase } from '../services/supabase';
import { summarizeWithGroq } from '../services/groq';
import NoteIcon from './NoteIcon';

const VECTOR_API_URL = "https://vector-search-api-398932329807.us-central1.run.app";

function VendorRagSearch({ onVendorSelect, onVendorView, selectedVendorId, onResults, onLoading, onError, onUserIntent, config = {} }) {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [userIntent, setUserIntent] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Default configuration
  const defaultConfig = {
    overlapBoost: 2.0,
    vectorWeight: 0.4,
    textWeight: 0.6,
    maxResults: 10,
    matchField: 'vendor_name',
    useLLM: true,
    useFullSearch: false,
    topN: 5
  };

  const searchConfig = { ...defaultConfig, ...config };

  // LLM configuration
  const llmConfig = {
    enabled: searchConfig.useLLM,
    topN: searchConfig.topN,
    prompt: `You are an outdoor recreation expert helping users find the most relevant businesses for their search query.

TASK: 
1. First, analyze the user's search intent and explain what type of outdoor activity/service they're looking for
2. Then, evaluate the businesses and return the top 3-5 most relevant ones based on:
   - Direct relevance to the user's search intent
   - Business type and activities offered 
   - Quality indicators (ratings, descriptions)
   - Geographic relevance and location preferences
   - Proximity to mentioned locations or regions

RESPONSE FORMAT - Return a JSON object with this exact structure:
{
  "userIntent": {
    "activity": "Primary activity/service the user is seeking",
    "location": "Specific location, region, or geographic preference mentioned or inferred",
    "reasoning": "Detailed explanation of what the LLM determined the user wants and why, including location analysis"
  },
  "filteredResults": [
    {"vendorName": "...", "metadata": {...}, "sources": [...], "finalScore": 0.95, "locationMatch": true/false, ...}
  ]
}

LOCATION ANALYSIS GUIDELINES:
- If user mentions specific cities, states, or regions, prioritize businesses in those areas
- If user mentions geographic features (mountains, coast, desert), match businesses in relevant regions
- Consider regional activity specialties (e.g., surfing for coastal areas, skiing for mountain regions)
- If no location specified, rank by overall relevance without geographic bias
- Add "locationMatch" field to each result indicating if it matches user's geographic intent

If no businesses are truly relevant, return empty filteredResults array but still provide userIntent analysis including any location preferences detected.`
  };

  const runSearch = async () => {
    if (!query.trim()) {
      throw new Error("Query cannot be empty");
    }

    setIsSearching(true);
    setHasSearched(true);
    onLoading?.(true);

    try {
      // Run both searches simultaneously
      const [vectorResponse, textResponse] = await Promise.all([
        // Vector search via Google Cloud Run API
        searchConfig.useFullSearch 
          ? fetch(`${VECTOR_API_URL}/search`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                query: query,
                top_k: searchConfig.maxResults,
                similarity_threshold: 0.0
              })
            })
          : fetch(`${VECTOR_API_URL}/search-simple?q=${encodeURIComponent(query)}&top_k=${searchConfig.maxResults}&similarity_threshold=0.0`),

        // Multi-field text search via Supabase with smart limiting
        (() => {
          const queryTerms = query.toLowerCase().split(' ').filter(term => term.length > 2);
          const queryComplexity = queryTerms.length;
          const hasSpecificTerms = queryTerms.some(term => 
            ['horse', 'climbing', 'kayaking', 'hiking', 'skiing', 'camping', 'biking', 'fishing'].includes(term)
          );
          
          let smartLimit = 150;
          if (queryComplexity >= 3 && queryComplexity <= 4) {
            smartLimit = 300;
          } else if (queryComplexity >= 5) {
            smartLimit = 500;
          }
          if (hasSpecificTerms) {
            smartLimit += 100;
          }
          smartLimit = Math.min(smartLimit, 600);
          
          return supabase
            .from("vendor_chunks")
            .select("ID, vendor_name, content, category, state, image_urls")
            .limit(smartLimit);
        })()
      ]);

      // Process vector search results
      let vectorData = { results: [] };
      if (vectorResponse.ok) {
        vectorData = await vectorResponse.json();
      } else {
        throw new Error("Vector search failed");
      }

      // Process text search results
      if (textResponse.error) {
        throw new Error("Text search failed: " + textResponse.error.message);
      }

      const allData = textResponse.data || [];
      
      // Client-side filtering with field-specific weighting (Enhanced)
      const queryLower = query.toLowerCase();
      const queryTermsFlexible = queryLower.split(' ').filter(term => term.length > 1); // More inclusive: length > 1 instead of > 2
      
      const scoredData = allData.map(item => {
        let fieldScore = 0;
        
        queryTermsFlexible.forEach(term => {
          // Exact matching (existing logic)
          if (item.vendor_name && item.vendor_name.toLowerCase().includes(term)) {
            fieldScore += 3.0;
          }
          if (item.category && item.category.toLowerCase().includes(term)) {
            fieldScore += 2.0;
          }
          if (item.state && item.state.toLowerCase().includes(term)) {
            fieldScore += 2.0;
          }
          if (item.content && item.content.toLowerCase().includes(term)) {
            fieldScore += 1.5;
          }
        });
        
        // Fallback for exact vendor name searches
        if (fieldScore === 0 && item.vendor_name) {
          const isExactVendorSearch = queryTermsFlexible.every(term => 
            item.vendor_name.toLowerCase().includes(term)
          );
          if (isExactVendorSearch) {
            fieldScore = 5.0; // High score for exact vendor matches
          }
        }
        
        return { ...item, fieldScore };
      });
      
      // More permissive filtering - allow zero scores through
      const filteredData = scoredData
        .filter(item => item.fieldScore >= 0)  // FIXED: Allow zero scores to prevent filtering out valid results
        .sort((a, b) => b.fieldScore - a.fieldScore)
        .slice(0, searchConfig.maxResults);

      // Calculate ideal results
      const idealResults = calculateIdealResultsFromData(
        vectorData.results || [], 
        filteredData,
        query
      );
      
      // Apply LLM filtering if enabled
      let finalResults = idealResults;
      let userIntent = null;

      if (llmConfig.enabled && idealResults.length > 0) {
        const llmResult = await filterWithLLM(idealResults, query);
        finalResults = llmResult.results;
        userIntent = llmResult.userIntent;
      }

      // Update internal state and call callbacks
      setResults(finalResults);
      setUserIntent(userIntent);
      onResults?.(finalResults);
      onUserIntent?.(userIntent);

    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
      setUserIntent(null);
      onError?.(err);
    } finally {
      setIsSearching(false);
      onLoading?.(false);
    }
  };

  // Calculate text similarity score with field-specific weighting
  const calculateTextSimilarity = (item, searchQuery) => {
    if (!item || !searchQuery) return 0;
    
    const queryTerms = searchQuery.toLowerCase().split(/\s+/).filter(term => term.length > 2);
    if (queryTerms.length === 0) return 0;
    
    let totalScore = 0;
    
    queryTerms.forEach(term => {
      let termScore = 0;
      
      if (item.vendor_name && item.vendor_name.toLowerCase().includes(term)) {
        const exactMatch = item.vendor_name.toLowerCase() === term ? 2 : 1;
        termScore += exactMatch * 3.0;
      }
      
      if (item.category && item.category.toLowerCase().includes(term)) {
        const exactMatch = item.category.toLowerCase().includes(term) ? 1 : 0.5;
        termScore += exactMatch * 2.0;
      }
      
      if (item.state && item.state.toLowerCase().includes(term)) {
        termScore += 2.0;
      }
      
      if (item.content && item.content.toLowerCase().includes(term)) {
        const contentLower = item.content.toLowerCase();
        const exactMatches = (contentLower.match(new RegExp(term, 'g')) || []).length;
        const termIndex = contentLower.indexOf(term);
        const proximityBonus = termIndex >= 0 ? Math.max(0, 1 - (termIndex / contentLower.length)) : 0;
        
        termScore += (exactMatches + proximityBonus * 0.3) * 1.5;
      }
      
      totalScore += termScore;
    });
    
    return Math.min(1, totalScore / (queryTerms.length * 3.0));
  };

  // Calculate ideal results from provided data
  const calculateIdealResultsFromData = (vectorData, textData, searchQuery = query) => {
    if (vectorData.length === 0 && textData.length === 0) return [];
    
    const combinedResults = [];
    const seenVendors = new Set();
    
    const checkOverlap = (vendorName, isVector) => {
      const targetList = isVector ? textData : vectorData;
      return targetList.some(other => {
        const otherName = isVector 
          ? other.vendor_name 
          : other.metadata?.vendor_name;
        return vendorName && otherName && vendorName === otherName;
      });
    };
    
    // Process vector results
    vectorData.forEach((result, index) => {
      const vendorName = result.metadata?.vendor_name;
      if (!vendorName || seenVendors.has(vendorName)) return;
      
      const isOverlap = checkOverlap(vendorName, true);
      const vectorScore = result.similarity_score || 0;
      const rankScore = (vectorData.length - index) / vectorData.length;
      
      let finalScore = vectorScore * searchConfig.vectorWeight * rankScore;
      if (isOverlap) finalScore *= searchConfig.overlapBoost;
      
      combinedResults.push({
        ...result,
        vendorName,
        sources: isOverlap ? ['Vector', 'Text'] : ['Vector'],
        finalScore,
        vectorScore,
        textScore: null,
        isOverlap
      });
      
      seenVendors.add(vendorName);
    });
    
    // Process text results with enhanced scoring
    textData.forEach((result, index) => {
      const vendorName = result.vendor_name;
      if (!vendorName || seenVendors.has(vendorName)) return;
      
      const isOverlap = checkOverlap(vendorName, false);
      
      const positionScore = (textData.length - index) / textData.length;
      const fieldWeightScore = result.fieldScore ? result.fieldScore / 10.0 : 0;
      const similarityScore = calculateTextSimilarity(result, searchQuery);
      
      const simulatedTsRank = Math.min(1.0, 
        similarityScore * 0.4 + fieldWeightScore * 0.3 + positionScore * 0.1);
      
      const combinedTextScore = (
        positionScore * 0.2 +
        simulatedTsRank * 0.3 +
        similarityScore * 0.2 +
        fieldWeightScore * 0.3
      );
      
      let finalScore = combinedTextScore * searchConfig.textWeight;
      if (isOverlap) finalScore *= searchConfig.overlapBoost;
      
      combinedResults.push({
        ...result,
        vendorName,
        sources: isOverlap ? ['Vector', 'Text'] : ['Text'],
        finalScore,
        vectorScore: null,
        textScore: combinedTextScore,
        textScoreBreakdown: {
          position: positionScore,
          tsRank: simulatedTsRank,
          similarity: similarityScore,
          combined: combinedTextScore
        },
        rank_score: simulatedTsRank,
        isOverlap,
        metadata: {
          ID: result.ID,
          vendor_name: result.vendor_name,
          category: result.category,
          state: result.state,
          content: result.content,
          image_urls: result.image_urls
        }
      });
      
      seenVendors.add(vendorName);
    });
    
    return combinedResults
      .sort((a, b) => b.finalScore - a.finalScore)
      .slice(0, searchConfig.maxResults);
  };

  // LLM filtering function
  const filterWithLLM = async (idealResultsList, searchQuery) => {
    if (!llmConfig.enabled || idealResultsList.length === 0) {
      return { results: idealResultsList, userIntent: null };
    }

    try {
      const topResults = idealResultsList.slice(0, llmConfig.topN);
      
      const businessData = topResults.map(result => ({
        vendorName: result.vendorName,
        category: result.metadata?.category,
        state: result.metadata?.state,
        content: result.metadata?.content,
        sources: result.sources,
        finalScore: result.finalScore,
        isOverlap: result.isOverlap
      }));

      const userMessage = `Search Query: "${searchQuery}"

Businesses to evaluate:
${JSON.stringify(businessData, null, 2)}

Please analyze and return the most relevant businesses for this search query.`;

      const response = await summarizeWithGroq("", {
        messages: [
          { role: "system", content: llmConfig.prompt },
          { role: "user", content: userMessage }
        ],
        temperature: 0.1,
        max_completion_tokens: 2000
      });

      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        return { results: [], userIntent: null };
      }
      
      const llmResponse = JSON.parse(jsonMatch[0]);
      const userIntent = llmResponse.userIntent;
      const filteredResults = llmResponse.filteredResults || [];
      
      const finalFiltered = filteredResults.map(llmResult => {
        const originalResult = topResults.find(r => r.vendorName === llmResult.vendorName);
        return originalResult ? {
          ...originalResult,
          llmRanked: true,
          llmScore: llmResult.finalScore || originalResult.finalScore
        } : null;
      }).filter(Boolean);

      return { results: finalFiltered, userIntent };

    } catch (error) {
      console.error('LLM filtering error:', error);
      return { results: [], userIntent: null };
    }
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search for outdoor activities, vendors, gear..."
          className="flex-1 border p-3 rounded text-gray-900 bg-white dark:text-gray-100 dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && query.trim() && !isSearching) {
              runSearch();
            }
          }}
        />
        <button 
          onClick={runSearch}
          disabled={isSearching || !query.trim()}
          className={`px-6 py-3 rounded font-medium transition-all duration-200 ${
            isSearching || !query.trim()
              ? 'bg-gray-400 cursor-not-allowed text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {isSearching ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* Loading State */}
      {isSearching && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Finding the best vendors for you...</p>
        </div>
      )}

      {/* Results */}
      {!isSearching && results.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Smart Search Results ({results.length})
          </h3>
          <div className="grid grid-cols-1 gap-4">
            {results.map((result, index) => {
              const vendor = result.metadata || result;
              const isSelected = selectedVendorId === vendor.ID;
              
              return (
                <div
                  key={index}
                  className="border rounded-lg p-4 transition-all duration-200 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md cursor-pointer"
                  onClick={() => onVendorView?.(vendor)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                        {vendor.vendor_name || 'Unknown Vendor'}
                      </h4>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        <span className="inline-block mr-4">
                          <strong>Category:</strong> {vendor.category || 'N/A'}
                        </span>
                        <span className="inline-block">
                          <strong>State:</strong> {vendor.state || 'N/A'}
                        </span>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 text-sm line-clamp-3">
                        {vendor.content || 'No description available.'}
                      </p>
                      {result.finalScore && (
                        <div className="mt-2 text-xs text-gray-500">
                          Relevance Score: {(result.finalScore * 100).toFixed(1)}%
                          {result.llmRanked && <span className="ml-2 text-blue-600">✨ AI Filtered</span>}
                        </div>
                      )}
                    </div>
                    {/* Radio button for Field Notes functionality */}
                    <div 
                      className="ml-4 flex items-center"
                      onClick={(e) => e.stopPropagation()} // Prevent card click when clicking radio area
                    >
                      <input
                        id={`field-notes-radio-search-${index}`}
                        type="radio"
                        name="field-notes-radio"
                        checked={isSelected}
                        onChange={() => onVendorSelect?.(vendor)}
                        className="form-radio h-4 w-4 text-blue-600 transition duration-150 ease-in-out mr-2"
                      />
                      <label 
                        htmlFor={`field-notes-radio-search-${index}`} 
                        className="flex items-center text-sm text-gray-800 dark:text-gray-100 select-none gap-1 cursor-pointer"
                      >
                        <span className="w-4 h-4 flex items-center justify-center">
                          <NoteIcon />
                        </span>
                        Use in Field Notes
                      </label>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No Results Message */}
      {!isSearching && hasSearched && results.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-600 dark:text-gray-400">
            No vendors found. Try different keywords or check your spelling.
          </p>
        </div>
      )}
    </div>
  );
}

export default VendorRagSearch;